/**
 * Recent-activity list: most recent payment intents + inbound payments
 * interleaved by created_at. Click an item to navigate to its detail.
 */
import { Link } from "react-router-dom";
import { ArrowDownToLine, Send } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { formatMethod, formatMinorUnits } from "@/lib/format";
import {
  INBOUND_STATE_BADGE_VARIANT,
  INTENT_STATE_BADGE_VARIANT,
} from "@/lib/state-badges";
import type { InboundPaymentRow, PaymentIntentRow } from "@/lib/api";
import { cn } from "@/lib/utils";

type FeedItem =
  | { kind: "intent"; row: PaymentIntentRow }
  | { kind: "inbound"; row: InboundPaymentRow };

interface ActivityFeedProps {
  intents: ReadonlyArray<PaymentIntentRow>;
  inbound: ReadonlyArray<InboundPaymentRow>;
  loading?: boolean;
  limit?: number;
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

export function ActivityFeed({ intents, inbound, loading, limit = 8 }: ActivityFeedProps) {
  const items: FeedItem[] = [
    ...intents.map<FeedItem>((row) => ({ kind: "intent", row })),
    ...inbound.map<FeedItem>((row) => ({ kind: "inbound", row })),
  ]
    .sort((a, b) => {
      const aT = new Date(a.row.created_at).getTime();
      const bT = new Date(b.row.created_at).getTime();
      return bT - aT;
    })
    .slice(0, limit);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-base font-semibold">Recent activity</CardTitle>
        <Link
          to="/payment-intents"
          className="text-xs font-medium text-muted-foreground hover:text-foreground"
        >
          View all →
        </Link>
      </CardHeader>
      <CardContent className="pt-0">
        {loading ? (
          <div className="space-y-3">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-3">
                <Skeleton className="h-8 w-8 rounded-full" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-3 w-1/2" />
                  <Skeleton className="h-2.5 w-1/3" />
                </div>
                <Skeleton className="h-4 w-16" />
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="py-6 text-center text-sm text-muted-foreground">
            No recent activity yet.
          </div>
        ) : (
          <ul className="divide-y divide-border/60">
            {items.map((item) => (
              <FeedRow key={`${item.kind}-${item.row.id}`} item={item} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function FeedRow({ item }: { item: FeedItem }) {
  const isIntent = item.kind === "intent";
  const Icon = isIntent ? Send : ArrowDownToLine;
  const href = isIntent ? `/payment-intents/${item.row.id}` : `/inbound-payments/${item.row.id}`;
  const stateVariant = isIntent
    ? INTENT_STATE_BADGE_VARIANT[(item.row as PaymentIntentRow).state]
    : INBOUND_STATE_BADGE_VARIANT[(item.row as InboundPaymentRow).state];
  const stateLabel = isIntent
    ? (item.row as PaymentIntentRow).state
    : (item.row as InboundPaymentRow).state;

  return (
    <li>
      <Link
        to={href}
        className="-mx-2 flex items-center gap-3 rounded-md px-2 py-3 transition-colors hover:bg-muted/50"
      >
        <div
          className={cn(
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
            isIntent ? "bg-info/10 text-info" : "bg-success/10 text-success",
          )}
        >
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium">
              {isIntent
                ? (item.row as PaymentIntentRow).tenant_name
                : (item.row as InboundPaymentRow).tenant_name}
            </span>
            <Badge dot variant={stateVariant} className="shrink-0 capitalize">
              {stateLabel.replace(/_/g, " ")}
            </Badge>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="min-w-0 truncate capitalize">
              {isIntent
                ? `${(item.row as PaymentIntentRow).direction} · ${formatMethod((item.row as PaymentIntentRow).method)}`
                : formatMethod((item.row as InboundPaymentRow).plugin)}
            </span>
            <span>·</span>
            <span className="shrink-0 whitespace-nowrap">{relativeTime(item.row.created_at)}</span>
          </div>
        </div>
        <div className="shrink-0 whitespace-nowrap text-right text-sm font-semibold tabular-nums">
          {formatMinorUnits(item.row.amount, item.row.currency)}
        </div>
      </Link>
    </li>
  );
}
